import React, { useEffect, useRef, type ReactNode } from "react";

import {
  getFirebaseToken,
  subscribeToForegroundMessages,
} from "./firebaseMessaging";
import { registerNotificationDevice } from "./notificationApi";
import { playNotificationSound, primeNotificationSound } from "util/notificationSound";

/* ────────────────────────────────────────────────────────────────────────
   NotificationProvider — Firebase Cloud Messaging bootstrapper.

   On mount (when enabled) it: asks for notification permission, generates the
   FCM device token (FID), registers it with the backend, and subscribes to
   foreground pushes. It does NOT hold notification state — the bell is the
   single source of truth (useNotificationBadge → /api/notifications/badge).
   A push simply fires `NOTIFICATIONS_REFRESH_EVENT` so the bell refetches and a
   chime plays. Foreground pushes arrive via onMessage; background pushes are
   handled by public/firebase-messaging-sw.js (OS banner) which also postMessages
   the open tabs so they refresh + chime. There is no polling.
   ──────────────────────────────────────────────────────────────────────── */

// Window event the notification bell listens for to refetch the badge.
export const NOTIFICATIONS_REFRESH_EVENT = "notifications:refresh";

interface NotificationProviderProps {
  children?: ReactNode;
  /** Skip setup until the user is authenticated/active. */
  enabled?: boolean;
}

export const NotificationProvider = ({ children, enabled = true }: NotificationProviderProps) => {
  // Note: guards against the FCM bootstrap running twice. React.StrictMode
  // (dev) mounts → unmounts → remounts, which fires this effect twice and
  // would duplicate the whole chain (installations → token → registrations →
  // device-token). The ref persists across that remount so init runs once.
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    let unsubscribe: (() => void) | undefined;

    const init = async () => {
      try {
        // 1. Ask permission + generate the FCM token (FID).
        const token = await getFirebaseToken();
        if (!token) {
          // No token → the backend can't push to this device, so NOTHING will
          // arrive (no foreground chime, no background banner). This is the most
          // common cause: permission not granted, blocked, or SW not registered.
          console.warn(
            "[notifications] No FCM token — permission:",
            typeof Notification !== "undefined" ? Notification.permission : "unsupported",
          );
          return;
        }

        // 2. Store the token on the backend for this user/device.
        await registerNotificationDevice({ fcmToken: token, deviceType: "WEB" });
        console.info("[notifications] FCM device registered; listening for pushes.");

        // 3. Foreground pushes (tab visible) → chime + tell the bell to refresh.
        unsubscribe = await subscribeToForegroundMessages((payload) => {
          console.info("[notifications] Foreground push received:", payload);
          playNotificationSound();
          window.dispatchEvent(new Event(NOTIFICATIONS_REFRESH_EVENT));
        });
      } catch (error) {
        console.error("Failed to initialize push notifications:", error);
      }
    };

    void init();

    return () => unsubscribe?.();
  }, [enabled]);

  // Unlock audio on the first user gesture so a notification arriving later can
  // chime without its own gesture (browsers start the AudioContext suspended).
  useEffect(() => {
    if (!enabled) return;
    const unlock = () => primeNotificationSound();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });

    // Dev helper: run `window.__testNotificationSound()` in the console to verify
    // audio works independently of a real push arriving.
    if (process.env.NODE_ENV !== "production") {
      (window as unknown as { __testNotificationSound?: () => void }).__testNotificationSound =
        playNotificationSound;
    }

    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [enabled]);

  // Background pushes are delivered to the service worker (not onMessage). The SW
  // posts a NOTIFICATIONS_REFRESH message to every open tab; forward it to the
  // bell as the same refresh event foreground pushes use, so the badge updates
  // on a background push without any polling. Kept in its own effect (no
  // one-time guard) so the listener is cleanly added/removed on enable/disable.
  useEffect(() => {
    if (!enabled) return;
    if (!("serviceWorker" in navigator)) return;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === "NOTIFICATIONS_REFRESH") {
        console.info("[notifications] Background push relayed from service worker.");
        // Chime here too: when the app tab is open but in the background, the OS
        // banner's sound is often muted (Do Not Disturb / sound off), so this is
        // the reliable audible cue for a different-tab notification.
        playNotificationSound();
        window.dispatchEvent(new Event(NOTIFICATIONS_REFRESH_EVENT));
      }
    };

    navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
  }, [enabled]);

  return <>{children}</>;
};

export default NotificationProvider;
